/**
 * setupSession — the browser's one reading of the setup-only mark (docs/PLAN-signin-requirements-20261029.md).
 *
 * A person whose sign-in requirement is overdue (or whose two-step was just reset with a code) still signs in, and
 * their ID token then carries SETUP_CLAIM. Firestore, Storage and every callable but the setup doors refuse such a
 * session, so the browser must send every protected page to the finish-setup screen and stop the app's always-on
 * readers before they sit on refusals. This module is where the browser learns the mark: one subscription to Firebase's
 * onIdTokenChanged, started once by the app (src/App.tsx), read by everything else through
 * src/hooks/useSignInSetupSession.ts.
 *
 * The mark is read exactly as the rules read it (`'p0_setup' in request.auth.token`): any value under the claim holds
 * the session, whatever it lists. Its kinds are read as the server's sessionClaims.ts reads them (vocabulary words
 * only, in vocabulary order), for the screen's own fallback when its status door cannot answer.
 *
 * While the watch is not running (a test, a page that never started it) nothing here holds anything back: `active` is
 * false and every reader carries on as it always did. A token that cannot be read is not a hold (the server and the
 * rules still refuse; the page only stops guessing).
 */
import { REQUIREMENT_KINDS, SETUP_CLAIM, type RequirementKind } from '../../types/signInSecurityVocabulary';

export interface SetupSession {
  /** The app started the watch. Off, nothing holds. */
  active: boolean;
  /** Whose token was read last (null: nobody is signed in, or nothing was heard yet). */
  uid: string | null;
  /** The claims of `uid`'s current token have been read. */
  checked: boolean;
  /** The token carries the setup-only mark. */
  held: boolean;
  /** What the mark lists as still to finish. */
  kinds: RequirementKind[];
}

/** The mark's kinds, as functions/src/auth/sessionClaims.ts reads them: vocabulary words only, in vocabulary order. */
export function setupKindsOfClaims(claims: Record<string, unknown> | null | undefined): RequirementKind[] {
  const raw = claims && typeof claims === 'object' ? claims[SETUP_CLAIM] : undefined;
  const listed = Array.isArray(raw) ? raw.filter((k): k is string => typeof k === 'string') : [];
  return REQUIREMENT_KINDS.filter((k) => listed.includes(k));
}

/** Does this token hold the session? Any value under the claim does, exactly as the rules read it. */
export function claimsHoldSetup(claims: Record<string, unknown> | null | undefined): boolean {
  return Boolean(claims && typeof claims === 'object' && Object.prototype.hasOwnProperty.call(claims, SETUP_CLAIM));
}

const OFF: SetupSession = { active: false, uid: null, checked: false, held: false, kinds: [] };

let state: SetupSession = OFF;
const listeners = new Set<() => void>();

function publish(next: SetupSession): void {
  state = next;
  listeners.forEach((listener) => listener());
}

/** The current reading (a stable object between changes, for useSyncExternalStore). */
export function readSetupSession(): SetupSession {
  return state;
}

export function subscribeSetupSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The part of a Firebase user this module reads. */
export interface TokenUser {
  uid: string;
  isAnonymous?: boolean;
  getIdTokenResult(): Promise<{ claims: Record<string, unknown> }>;
}

/** Where tokens come from: Firebase's onIdTokenChanged by default; a test hands its own. Returns its unsubscribe. */
export type TokenSource = (next: (user: TokenUser | null) => void) => () => void;

const firebaseTokens: TokenSource = (next) => {
  let unsubscribe: (() => void) | null = null;
  let stopped = false;
  // On demand: firebase/auth is already on the page wherever the app runs, and a module that only reads this store
  // (a header, a provider) must never pull it in at import (the chat-isolation suites' trap).
  void import('firebase/auth')
    .then(({ getAuth, onIdTokenChanged }) => {
      if (!stopped) unsubscribe = onIdTokenChanged(getAuth(), (user) => next(user));
    })
    .catch(() => undefined);
  return () => {
    stopped = true;
    unsubscribe?.();
  };
};

/** How long anything waits for the mark's reading before it carries on without it. */
export const SETUP_READING_WAIT_MS = 8000;

let stopWatch: (() => void) | null = null;

/**
 * Start reading every token the app's Firebase session gets (once; a second call returns the same stop). A new token
 * for the same person keeps the last reading until its own is read, so an hourly refresh never flickers anything.
 *
 * Bounded both ways, so nothing that waits on a reading ever waits for good: a token not read within
 * SETUP_READING_WAIT_MS reads as no mark (as an unreadable one does; a later answer still counts), and a watch that
 * hears nothing at all in that time turns itself off until it does.
 */
export function startSetupSessionWatch(source: TokenSource = firebaseTokens, waitMs: number = SETUP_READING_WAIT_MS): () => void {
  if (stopWatch) return stopWatch;
  publish({ ...OFF, active: true });
  let heard = false;
  let generation = 0;
  let readingTimer: ReturnType<typeof setTimeout> | null = null;
  const stopReadingTimer = () => {
    if (readingTimer !== null) clearTimeout(readingTimer);
    readingTimer = null;
  };
  const silence = setTimeout(() => {
    if (!heard) publish(OFF);
  }, waitMs);
  const unsubscribe = source((user) => {
    heard = true;
    clearTimeout(silence);
    generation += 1;
    const asked = generation;
    stopReadingTimer();
    if (!user || user.isAnonymous) {
      publish({ active: true, uid: null, checked: true, held: false, kinds: [] });
      return;
    }
    if (state.uid !== user.uid || !state.active) {
      publish({ active: true, uid: user.uid, checked: false, held: false, kinds: [] });
      readingTimer = setTimeout(() => {
        readingTimer = null;
        if (asked === generation && !state.checked) publish({ active: true, uid: user.uid, checked: true, held: false, kinds: [] });
      }, waitMs);
    }
    user.getIdTokenResult().then(
      (result) => {
        if (asked !== generation) return;
        stopReadingTimer();
        const claims = result?.claims ?? {};
        publish({ active: true, uid: user.uid, checked: true, held: claimsHoldSetup(claims), kinds: setupKindsOfClaims(claims) });
      },
      () => {
        if (asked !== generation) return;
        stopReadingTimer();
        publish({ active: true, uid: user.uid, checked: true, held: false, kinds: [] });
      },
    );
  });
  stopWatch = () => {
    clearTimeout(silence);
    stopReadingTimer();
    unsubscribe();
    stopWatch = null;
    publish(OFF);
  };
  return stopWatch;
}

/**
 * The reading a page or reader may go on with: a signed-in person's token read, without the mark. Keyed by the
 * session, not by a reader's own uid: the app's readers key on the account id the sign-in resolved, which can differ
 * from the token's uid (a canonical mapping), and a reader must never wait for good on a uid the token will not name.
 */
export function readingClear(s: SetupSession): boolean {
  return s.uid !== null && s.checked && !s.held;
}

/**
 * For a reader that starts on its own (ProtectedRoute's user context): true once `uid`'s token is read and holds the
 * session, false when it does not, when the watch is off, or when no reading arrives within SETUP_READING_WAIT_MS.
 */
export function whenSetupKnown(uid: string, waitMs: number = SETUP_READING_WAIT_MS): Promise<boolean> {
  const settled = (s: SetupSession): boolean | null => (!s.active ? false : s.uid === uid && s.checked ? s.held : null);
  const now = settled(state);
  if (now !== null) return Promise.resolve(now);
  return new Promise((resolve) => {
    let done = false;
    const finish = (held: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      off();
      resolve(held);
    };
    const off = subscribeSetupSession(() => {
      const answer = settled(state);
      if (answer !== null) finish(answer);
    });
    const timer = setTimeout(() => finish(false), waitMs);
  });
}

/** Tests only: stop the watch and forget every reading. */
export function resetSetupSessionForTests(): void {
  if (stopWatch) stopWatch();
  publish(OFF);
}
